// === FILE HEADER: Sound Utility Functions ===
// This file provides sound effect utilities for the Chess Slider SPA, including fallback beep logic and file-based playback.
// Structure and section anchors follow project documentation and copilot-instructions.md.
// Last updated: 2025-07-15
//
// === END FILE HEADER ===
// === COPILOT FILE RULES ===
// DO NOT DELETE THE HEADER OR FILE RULES FROM THE FILE
// DO NOT PLACE ANY CODE, HOOKS, OR LOGIC ABOVE IMPORTS. 
// All import statements must be at the very top of the file, before any other code or comments (except for a file-level comment header or license block, if present).
// React hooks (useEffect, useState, etc.) must only be called inside a React function component or custom hook, never at the top level.
// Make a copy of the file in copies folder before doing any edits.
// Updating full large files has been difficult.  You may use the copy to refer to if it helps you.
// After completing edits compare the edited file to the original and make sure nothing other than the intended changes have happened
// If there are unintended changes repair the file
// After user approves the copy may be deleted
// DO NOT USE INLINE STYLES ALL STYLES MUST BE DEFINED IN THE PROPER FILE AND IMPORTED
// === END COPILOT FILE RULES ===
// === Section: Sound Utility ===
// Utility for playing sound effects for game events.
// Falls back to Web Audio API beeps if sound files are not available.

import { NOTE_SEQUENCES, SOUND_FILES } from './soundConstants';
import { logFunctionCall } from './spaLogger';

function playBeep(type = 'moved', volume = 1.0, onEnd) {
  logFunctionCall && logFunctionCall('SoundUtils', 'playBeep', { type, volume });
  // Use note sequence from constants and playNoteSequence
  const sequence = NOTE_SEQUENCES[type] || NOTE_SEQUENCES['moved'];
  try {
    playNoteSequence(sequence, volume, onEnd);
  } catch (e) {
    if (typeof onEnd === 'function') onEnd();
  }
}

function fileExists(url, callback) {
  // Check if a file exists by attempting to fetch its HEAD
  fetch(url, { method: 'HEAD' })
    .then(res => callback(res.ok))
    .catch(() => callback(false));
}

export function playSound(type = 'moved', volume = 1.0, onEnd) {
  logFunctionCall && logFunctionCall('SoundUtils', 'playSound', { type, volume });
  const fileUrl = SOUND_FILES[type];
  if (fileUrl) {
    fileExists(fileUrl, exists => {
      if (exists) {
        const audio = new window.Audio(fileUrl);
        audio.volume = volume;
        audio.onended = () => { if (typeof onEnd === 'function') onEnd(); };
        audio.play().catch(() => playBeep(type, volume, onEnd));
      } else {
        playBeep(type, volume, onEnd);
      }
    });
  } else {
    playBeep(type, volume, onEnd);
  }
}

// === Section: Random Note Generator ===
/**
 * Generates a random note sequence string for shuffle sounds.
 * @param {number} count - Number of notes in the sequence
 * @param {number} [minFreq=220] - Minimum frequency (Hz)
 * @param {number} [maxFreq=1760] - Maximum frequency (Hz)
 * @param {number} [duration=0.1] - Duration of each note (seconds)
 * @param {number} [pause=0.05] - Pause after each note (seconds)
 * @returns {string} Note sequence string
 */
export function randomNoteGenerator(count, minFreq = 220, maxFreq = 1760, duration = 0.1, pause = 0.05) {
  let seq = '';
  for (let i = 0; i < count; i++) {
    const freq = Math.random() * (maxFreq - minFreq) + minFreq;
    seq += `${freq.toFixed(2)}:${duration};0:${pause};`;
  }
  return seq;
}
// === End Section: Random Note Generator ===

// === End Section: Sound Utility ===

// === Section: Note Sequence Player ===
/**
 * Plays a sequence of notes using the Web Audio API.
 * Each note string: "frequency:duration[+/-volumeChange];"
 * Example: "800:0.10+0.2;521:0.5-0.1;"
 * @param {string|Array} sequence - String or array of note objects.
 * @param {number} baseVolume - Base volume (0-1, default 1)
 * @param {function} onEnd - Optional callback when sequence ends
 */
export function playNoteSequence(sequence, baseVolume = 1, onEnd) {
  logFunctionCall && logFunctionCall('SoundUtils', 'playNoteSequence', { sequence, baseVolume });
  // Parse sequence into array of {frequency, duration, volumeAdj}
  let notes = [];
  if (typeof sequence === 'string') {
    const noteStrings = sequence.split(';').filter(Boolean);
    for (const noteStr of noteStrings) {
      // Match: freq:dur(+/-volAdj)?
      const match = noteStr.match(/^\d+(?:\.\d+)?:\d+(?:\.\d+)?([+-]\d+(?:\.\d+)?)?$/);
      if (match) {
        const parts = noteStr.split(':');
        const frequency = parseFloat(parts[0]);
        const duration = parseFloat(parts[1]);
        const volumeAdj = parts[2] ? parseFloat(parts[2]) : 0;
        notes.push({ frequency, duration, volumeAdj });
      }
    }
  } else if (Array.isArray(sequence)) {
    notes = sequence.map(n => ({
      frequency: n.frequency,
      duration: n.duration,
      volumeAdj: n.volumeAdj || 0
    }));
  }
  if (!notes.length) {
    if (typeof onEnd === 'function') onEnd();
    return;
  }

  let ctx = new (window.AudioContext || window.webkitAudioContext)();
  let time = ctx.currentTime;
  let gain = ctx.createGain();
  gain.connect(ctx.destination);

  notes.forEach((note, i) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = note.frequency;
    // Clamp volume between 0 and 1
    const noteVolume = Math.max(0, Math.min(1, baseVolume + note.volumeAdj));
    gain.gain.setValueAtTime(0.15 * noteVolume, time);
    gain.gain.exponentialRampToValueAtTime(0.01, time + note.duration);
    osc.connect(gain);
    osc.start(time);
    osc.stop(time + note.duration);
    if (i === notes.length - 1) {
      osc.onended = () => {
        ctx.close();
        if (typeof onEnd === 'function') onEnd();
      };
    }
    time += note.duration;
  });
}
// === End Section: Note Sequence Player ===
